// =============================================
// SERP Research Service
// v3.0: DataForSEO SERP + Crawl4AI extraction + Pruning/BM25 filtering
// Pruning as default (better for SERP research), BM25 as fallback
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { getOrganicResults } from "@/services/dataForSeoService";
import { logPublic, logDebug, logVerbose, logError } from "./contentLogger";
import { getApiBase } from "@/lib/apiBase";
import { env } from "@/lib/env";
import { getOwnDomain, isSameDomain } from "./ownDomain";

// Crawl4AI endpoint — adapter handles browser (/crawl4ai via nginx)
// vs worker (http://crawl4ai:11235 via Coolify internal DNS).
const CRAWL4AI_URL = getApiBase("crawl4ai");

// =============================================
// GeoNode residential proxy fallback (Crawl4AI v0.4+ HTTP API)
// =============================================
// Pattern: pierwsza proba bez proxy (szybsza, bez billingu GeoNode).
// Jezeli Crawl4AI zwroci blocked/empty/timeout -> retry z proxy_config
// w body (browser_config.params.proxy_config).
//
// Env vars (server-side, Coolify deploy):
//   VITE_GEONODE_PROXY_SERVER   = http://proxy.geonode.io:9000
//   VITE_GEONODE_PROXY_USERNAME = geonode_<username>-type-residential
//   VITE_GEONODE_PROXY_PASSWORD = <secret>
//
// Logi (logPublic): direct attempt, proxy fallback trigger reason,
// proxy success/fail. Forensics: URLDebug.via_proxy flag persisted.

interface GeoNodeProxyConfig {
  server: string;
  username: string;
  password: string;
}

function getGeoNodeProxyConfig(): GeoNodeProxyConfig | null {
  const server = env("VITE_GEONODE_PROXY_SERVER");
  const username = env("VITE_GEONODE_PROXY_USERNAME");
  const password = env("VITE_GEONODE_PROXY_PASSWORD");
  if (!server || !username || !password) return null;
  return { server, username, password };
}

const GEONODE_PROXY = getGeoNodeProxyConfig();
const PROXY_AVAILABLE = GEONODE_PROXY !== null;

// One-time startup log (DevTools console — visible po refresh)
if (PROXY_AVAILABLE) {
  // eslint-disable-next-line no-console
  console.log(`[SERP] GeoNode proxy fallback ENABLED — server=${GEONODE_PROXY!.server}`);
} else {
  // eslint-disable-next-line no-console
  console.log("[SERP] GeoNode proxy fallback DISABLED — set VITE_GEONODE_PROXY_{SERVER,USERNAME,PASSWORD} in Coolify env");
}

// Logger-friendly status string (used in researchSERP() per-call, visible in UI log panel)
function getProxyStatusLine(): string {
  return PROXY_AVAILABLE
    ? `GeoNode proxy fallback: ENABLED (server=${GEONODE_PROXY!.server})`
    : `GeoNode proxy fallback: DISABLED (brak VITE_GEONODE_PROXY_* env w Coolify)`;
}

const PROXY_BLOCK_KEYWORDS = [
  "err_connection_reset", "connection reset", "net::err_",
  "timeout", "blocked", "connection refused", "abort",
  "403", "429", "503", "521", "522", "523", "525",
];

function shouldRetryWithProxy(opts: {
  ok: boolean;
  status?: number;
  errorMessage?: string;
  charsTotal: number;
}): { retry: boolean; reason: string } {
  if (!PROXY_AVAILABLE) return { retry: false, reason: "proxy_not_configured" };
  // HTTP error or status anti-bot → retry
  if (!opts.ok) {
    if (typeof opts.status === "number" && [403, 429, 503, 521, 522, 523, 525].includes(opts.status)) {
      return { retry: true, reason: `http_${opts.status}` };
    }
    return { retry: true, reason: `http_${opts.status ?? "fail"}` };
  }
  // Empty response (anti-bot returned empty body)
  if (opts.charsTotal === 0) return { retry: true, reason: "empty_content" };
  // Error keyword match (timeout, connection reset, blocked, etc.)
  const lower = (opts.errorMessage ?? "").toLowerCase();
  if (lower && PROXY_BLOCK_KEYWORDS.some((kw) => lower.includes(kw))) {
    return { retry: true, reason: `error_kw_${lower.slice(0, 40)}` };
  }
  return { retry: false, reason: "ok" };
}

// Build Crawl4AI v0.4+ payload (with optional proxy_config)
function buildCrawlPayload(url: string, opts: { withProxy: boolean; minBlockWords: number }) {
  const browserParams: Record<string, unknown> = {
    headless: true,
    verbose: false,
  };
  if (opts.withProxy && GEONODE_PROXY) {
    browserParams.proxy_config = {
      server: GEONODE_PROXY.server,
      username: GEONODE_PROXY.username,
      password: GEONODE_PROXY.password,
    };
  }
  return {
    urls: [url],
    browser_config: { type: "BrowserConfig", params: browserParams },
    crawler_config: {
      type: "CrawlerRunConfig",
      params: {
        cache_mode: "bypass",
        word_count_threshold: opts.minBlockWords,
        excluded_tags: ["nav", "footer", "header", "aside", "script", "style"],
        only_text: true,
      },
    },
    // Top-level mirror (back-compat dla starszych Crawl4AI < 0.8 — przyjmowało pola na płaskim poziomie)
    word_count_threshold: opts.minBlockWords,
    excluded_tags: ["nav", "footer", "header", "aside", "script", "style"],
    only_text: true,
  };
}
const CRAWL_TIMEOUT_MS = 30_000;
const MAX_CONTENT_CHARS = 5000;
const MIN_BLOCK_WORDS = 50;
const PRUNING_THRESHOLD = 0.4;
const BM25_FALLBACK_THRESHOLD = 0.8;
const MAX_SOURCES = 10;

// Skip URLs that won't have useful article content
const BLOCKED_DOMAINS = [
  "youtube.com", "youtu.be", "vimeo.com",
  "facebook.com", "twitter.com", "x.com", "instagram.com", "pinterest.com",
  "tiktok.com", "linkedin.com", "reddit.com",
];
const BLOCKED_EXTENSIONS = [".pdf", ".doc", ".docx", ".ppt", ".xls", ".zip", ".rar", ".jpg", ".png", ".gif"];

function shouldSkipUrl(url: string): boolean {
  const lower = url.toLowerCase();
  const domain = lower.replace(/https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  if (BLOCKED_DOMAINS.some((d) => domain.includes(d))) return true;
  if (BLOCKED_EXTENSIONS.some((ext) => lower.endsWith(ext))) return true;
  return false;
}

// =============================================
// T2F4 Content Cleaner — based on kurs-prompty/T2F4-content_cleaner.py
// =============================================

const BLACKLIST_PHRASES = [
  // Nawigacja / UI
  "koszyk", "menu:", "filters", "loading", "show results", "czytaj dalej",
  "zobacz więcej", "pokaż więcej", "rozwiń", "zwiń", "wróć", "przejdź do",
  // E-commerce
  "dodaj do koszyka", "kup teraz", "zamów", "cena:", "zł z kodem",
  "rabat", "promocja", "darmowa dostawa", "bezpłatna dostawa",
  // Cookies / RODO
  "cookies", "ciasteczka", "polityka prywatności", "rodo", "zgoda na",
  "akceptuję", "ustawienia cookie", "pliki cookie",
  // Formularze / Logowanie
  "zaloguj", "zarejestruj", "newsletter", "zapisz się", "subskrybuj",
  "podaj email", "podaj e-mail", "wyślij formularz",
  // Social
  "facebook", "instagram", "twitter", "udostępnij", "polub",
  // Aplikacje
  "zainstaluj aplikację", "pobierz aplikację", "app store", "google play",
  // Inne
  "something went wrong", "brak produktów", "wyszukiwarka",
  "kontynuuj zakupy", "potwierdź płatność", "blik",
];

const MIN_PARAGRAPH_LENGTH = 60;

/**
 * Step 1: Remove duplicate lines (T2F4 remove_duplicate_lines)
 */
function removeDuplicateLines(text: string): string {
  const lines = text.split("\n");
  const seen = new Set<string>();
  const result: string[] = [];
  for (const line of lines) {
    const stripped = line.trim();
    if (!stripped) { result.push(line); continue; }
    const normalized = stripped.toLowerCase().replace(/\s+/g, " ");
    if (!seen.has(normalized)) {
      seen.add(normalized);
      result.push(line);
    }
  }
  return result.join("\n");
}

/**
 * Step 2+3: Blacklist + min length filter (T2F4 remove_blacklisted_paragraphs)
 * Returns { cleaned, blacklistRemoved }
 */
function cleanContent(text: string): { cleaned: string; blacklistRemoved: number } {
  // First: dedup lines
  const deduped = removeDuplicateLines(text);

  const paragraphs = deduped.split(/\n\n+/);
  let blacklistRemoved = 0;
  const kept: string[] = [];

  for (const para of paragraphs) {
    const stripped = para.trim();
    // Min length
    if (stripped.length < MIN_PARAGRAPH_LENGTH) continue;
    // Blacklist check
    const lower = stripped.toLowerCase();
    if (BLACKLIST_PHRASES.some((phrase) => lower.includes(phrase))) {
      blacklistRemoved++;
      continue;
    }
    // Navigation-like blocks (many short lines)
    const lines = stripped.split("\n");
    if (lines.length > 3 && lines.every((l) => l.trim().length < 30)) continue;
    kept.push(stripped);
  }

  return { cleaned: kept.join("\n\n"), blacklistRemoved };
}

// =============================================
// Cross-block dedup via cosine similarity (T2F4 find_diverse_blocks_with_stats)
// Uses simple Jaccard on word trigrams — no OpenAI embeddings needed in browser
// =============================================

const BLOCK_SIMILARITY_THRESHOLD = 0.85;
const LENGTH_PROTECTION_THRESHOLD = 0.30;
const TARGET_CHAR_LIMIT = 50_000;

function wordTrigrams(text: string): Set<string> {
  const words = text.toLowerCase().replace(/[^\w\sąćęłńóśźż]/g, "").split(/\s+/).filter((w) => w.length > 2);
  const trigrams = new Set<string>();
  for (let i = 0; i <= words.length - 3; i++) {
    trigrams.add(words.slice(i, i + 3).join(" "));
  }
  return trigrams;
}

function trigramSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) { if (b.has(t)) intersection++; }
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

interface BlockDedupResult {
  kept: ContentBlock[];
  removed: number;
}

/**
 * Deduplicate content blocks by similarity.
 * If two blocks are > 0.85 similar → keep longer (with length protection).
 * Also enforces TARGET_CHAR_LIMIT.
 */
function deduplicateBlocks(blocks: ContentBlock[]): BlockDedupResult {
  if (blocks.length <= 1) return { kept: blocks, removed: 0 };

  // Sort by relevanceScore desc (best first)
  const sorted = [...blocks].sort((a, b) => b.relevanceScore - a.relevanceScore);
  const kept: ContentBlock[] = [];
  const keptTrigrams: Array<{ trigrams: Set<string>; length: number }> = [];
  let totalChars = 0;
  let removed = 0;

  for (const block of sorted) {
    const blockTrigrams = wordTrigrams(block.text);
    let isDuplicate = false;

    for (const existing of keptTrigrams) {
      const sim = trigramSimilarity(blockTrigrams, existing.trigrams);
      if (sim > BLOCK_SIMILARITY_THRESHOLD) {
        // Length protection: if length difference > 30% and sim < 0.95 → keep both
        const lengthDiff = Math.abs(block.text.length - existing.length) / Math.max(block.text.length, existing.length);
        if (lengthDiff > LENGTH_PROTECTION_THRESHOLD && sim < 0.95) {
          continue; // Keep despite similarity
        }
        isDuplicate = true;
        break;
      }
    }

    if (isDuplicate) {
      removed++;
      continue;
    }

    // Char limit
    if (totalChars + block.text.length > TARGET_CHAR_LIMIT) {
      removed++;
      continue;
    }

    kept.push(block);
    keptTrigrams.push({ trigrams: blockTrigrams, length: block.text.length });
    totalChars += block.text.length;
  }

  return { kept, removed };
}

// =============================================
// Types
// =============================================

export interface ContentBlock {
  url: string;
  text: string;
  wordCount: number;
  relevanceScore: number;
}

export interface URLDebug {
  url: string;
  crawled: boolean;
  chars_total: number;
  chars_kept: number;
  error?: string;
  via_proxy?: boolean; // true = served by GeoNode fallback, false/undefined = direct
}

export interface SERPDebug {
  query: string;
  urls_fetched: number;
  urls_crawled: number;
  urls_failed: number;
  per_url: URLDebug[];
  filter_method: string;
  threshold_used: number;
  blocks_passed: number;
  blocks_rejected: number;
  proxy_stats?: { direct_success: number; proxy_success: number; both_failed: number; proxy_available: boolean };
}

export interface SERPResult {
  serpUrls: string[];
  cleanContent: ContentBlock[];
  cacheHit: boolean;
  filterMethod: string;
  debug: SERPDebug;
}

// =============================================
// Pruning Content Filter (default — text density analysis, no query needed)
// =============================================

function pruningScore(text: string): number {
  const words = text.split(/\s+/).length;
  if (words < MIN_BLOCK_WORDS) return 0;

  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 10).length;
  const sentenceDensity = sentences / Math.max(words / 100, 1);
  const paragraphs = text.split(/\n\n+/).filter((p) => p.trim().length > 20).length;
  const linkPatterns = (text.match(/https?:\/\/|www\.|click here|czytaj więcej|dowiedz się/gi) || []).length;
  const linkPenalty = Math.min(linkPatterns * 0.3, 2);

  return Math.log2(words) * 0.5 + sentenceDensity * 2 + paragraphs * 0.3 - linkPenalty;
}

// =============================================
// BM25 (fallback only)
// =============================================

function tokenize(text: string): string[] {
  return text.toLowerCase().replace(/[^\w\sąćęłńóśźż]/g, " ").split(/\s+/).filter((w) => w.length > 2);
}

function computeBM25(query: string, document: string, avgDocLen: number, k1 = 1.5, b = 0.75): number {
  const queryTokens = tokenize(query);
  const docTokens = tokenize(document);
  if (docTokens.length === 0) return 0;

  const tf = new Map<string, number>();
  for (const token of docTokens) tf.set(token, (tf.get(token) || 0) + 1);

  let score = 0;
  for (const qToken of queryTokens) {
    const freq = tf.get(qToken) || 0;
    if (freq === 0) continue;
    score += (freq * (k1 + 1)) / (freq + k1 * (1 - b + b * (docTokens.length / avgDocLen)));
  }
  return score;
}

// =============================================
// Crawl4AI extraction (via proxy)
// =============================================

// Single attempt to Crawl4AI — used by extractContent() with retry-via-proxy logic
async function crawlOnce(
  url: string,
  domain: string,
  opts: { withProxy: boolean },
): Promise<{ content: string | null; chars_total: number; error?: string; status?: number; ok: boolean }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CRAWL_TIMEOUT_MS);
  const tag = opts.withProxy ? "via GeoNode" : "direct";

  try {
    const payload = buildCrawlPayload(url, { withProxy: opts.withProxy, minBlockWords: MIN_BLOCK_WORDS });
    const response = await fetch(`${CRAWL4AI_URL}/crawl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      return {
        content: null,
        chars_total: 0,
        error: `HTTP ${response.status}: ${errText.slice(0, 100)}`,
        status: response.status,
        ok: false,
      };
    }

    const result = await response.json();
    const firstResult = result?.results?.[0] || result?.result || {};
    logVerbose("SERP", `${tag} response from ${domain}`, {
      topKeys: Object.keys(result || {}),
      resultKeys: Object.keys(firstResult),
      markdownType: typeof firstResult.markdown,
      cleanedTextType: typeof firstResult.cleaned_text,
      htmlLength: typeof firstResult.html === "string" ? firstResult.html.length : "N/A",
    });

    // Extract text — handle different Crawl4AI response formats
    let raw = "";
    if (typeof firstResult.markdown === "string") {
      raw = firstResult.markdown;
    } else if (firstResult.markdown && typeof firstResult.markdown === "object") {
      const mdObj = firstResult.markdown;
      const fitMd = mdObj.fit_markdown;
      const rawMd = mdObj.raw_markdown;
      if (typeof fitMd === "string" && fitMd.length > 0) raw = fitMd;
      else if (typeof rawMd === "string" && rawMd.length > 0) raw = rawMd;
    }
    if (!raw && typeof firstResult.cleaned_text === "string") raw = firstResult.cleaned_text;
    if (!raw && typeof firstResult.extracted_content === "string") raw = firstResult.extracted_content;
    if (!raw && typeof firstResult.text === "string") raw = firstResult.text;
    if (!raw && typeof firstResult.html === "string") {
      raw = firstResult.html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
        .replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    }

    const chars_total = raw.length;
    const content = raw.slice(0, MAX_CONTENT_CHARS * 2);
    if (chars_total === 0) {
      return {
        content: null,
        chars_total: 0,
        error: `empty_response (fields: ${Object.keys(firstResult).join(",")})`,
        status: response.status,
        ok: true, // HTTP 200 ale empty
      };
    }
    return { content, chars_total, status: response.status, ok: true };
  } catch (err) {
    const errorType = err instanceof Error && err.name === "AbortError" ? "timeout" : "fetch_error";
    return {
      content: null,
      chars_total: 0,
      error: `${errorType}: ${err instanceof Error ? err.message : "unknown"}`,
      ok: false,
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Kontrola zycia URL-a przez Crawl4AI (2026-08-23): weryfikator zrodel w ~7%
 * przypadkow wpisywal zmyslony adres (audyt: 4/60 zrodel = 404). Zwraca:
 *   false  = strona MARTWA (tylko 404/410 — twarde dowody),
 *   true   = odpowiada (2xx/3xx, takze 403 — bot-block to nie brak strony),
 *   null   = nie udalo sie sprawdzic (fail-open, nie wycinamy).
 */
export async function checkUrlAlive(url: string): Promise<boolean | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20_000);
  try {
    const payload = buildCrawlPayload(url, { withProxy: false, minBlockWords: MIN_BLOCK_WORDS });
    const response = await fetch(`${CRAWL4AI_URL}/crawl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const result = await response.json();
    const first = result?.results?.[0] || result?.result || {};
    const sc = typeof first.redirected_status_code === "number" && first.redirected_status_code > 0
      ? first.redirected_status_code
      : first.status_code;
    if (typeof sc !== "number" || sc === 0) return null;
    return !(sc === 404 || sc === 410);
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function extractContent(url: string): Promise<{ content: string | null; chars_total: number; error?: string; via_proxy: boolean }> {
  const domain = url.replace(/https?:\/\//, "").split("/")[0];
  logDebug("SERP", `Crawl4AI direct → ${domain}`, { url: `${CRAWL4AI_URL}/crawl` });

  // Attempt 1: direct (no proxy)
  const direct = await crawlOnce(url, domain, { withProxy: false });
  const retryDecision = shouldRetryWithProxy({
    ok: direct.ok,
    status: direct.status,
    errorMessage: direct.error,
    charsTotal: direct.chars_total,
  });

  if (!retryDecision.retry) {
    if (direct.content) {
      logPublic("SERP", `✓ ${domain} direct — ${direct.chars_total} znaków raw, ${direct.content.length} kept`);
      return { content: direct.content, chars_total: direct.chars_total, via_proxy: false };
    }
    // No content + no retry path (proxy not configured or non-retriable error)
    if (!PROXY_AVAILABLE) {
      logError("SERP", `✗ ${domain} direct failed — proxy NOT configured (set VITE_GEONODE_PROXY_*) — error: ${direct.error}`);
    } else {
      logError("SERP", `✗ ${domain} direct failed (non-retriable: ${retryDecision.reason}) — error: ${direct.error}`);
    }
    return { content: null, chars_total: 0, error: direct.error, via_proxy: false };
  }

  // Attempt 2: via GeoNode proxy
  logPublic("SERP", `⚠ ${domain} direct blocked (${retryDecision.reason}) → retry via GeoNode proxy...`);
  const proxied = await crawlOnce(url, domain, { withProxy: true });

  if (proxied.content) {
    logPublic("SERP", `✓ ${domain} via GeoNode — ${proxied.chars_total} znaków raw, ${proxied.content.length} kept`);
    return { content: proxied.content, chars_total: proxied.chars_total, via_proxy: true };
  }

  logError("SERP", `✗ ${domain} BOTH attempts failed — direct: ${direct.error} | proxy: ${proxied.error}`);
  return {
    content: null,
    chars_total: 0,
    error: `direct: ${direct.error} | proxy: ${proxied.error}`,
    via_proxy: true,
  };
}

async function simpleFetch(url: string): Promise<{ content: string | null; chars_total: number; error?: string }> {
  const domain = url.replace(/https?:\/\//, "").split("/")[0];
  logDebug("SERP", `simpleFetch fallback → ${domain}`);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15_000);

    const response = await fetch(`${CRAWL4AI_URL}/crawl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ urls: [url], only_text: true }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      logError("SERP", `simpleFetch ✗ ${domain} — HTTP ${response.status}`);
      return { content: null, chars_total: 0, error: `fallback_HTTP_${response.status}` };
    }

    const result = await response.json();
    const firstResult = result?.results?.[0] || result?.result || {};
    let raw = "";
    if (typeof firstResult.markdown === "string") raw = firstResult.markdown;
    else if (firstResult.markdown && typeof firstResult.markdown === "object") {
      raw = firstResult.markdown.fit_markdown || firstResult.markdown.raw_markdown || "";
      if (typeof raw !== "string") raw = "";
    }
    if (!raw && typeof firstResult.cleaned_text === "string") raw = firstResult.cleaned_text;
    if (!raw && typeof firstResult.extracted_content === "string") raw = firstResult.extracted_content;
    if (!raw && typeof firstResult.html === "string") raw = firstResult.html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

    logDebug("SERP", `simpleFetch ${domain} — ${raw.length} znaków`);
    return { content: raw.length > 0 ? raw.slice(0, MAX_CONTENT_CHARS * 2) : null, chars_total: raw.length };
  } catch (err) {
    logError("SERP", `simpleFetch ✗ ${domain}`, err);
    return { content: null, chars_total: 0, error: `fallback_error: ${err instanceof Error ? err.message : "unknown"}` };
  }
}

// =============================================
// Cache
// =============================================

async function getCachedSERP(nodeId: string, query: string): Promise<{ results: unknown[]; extractedContent: ContentBlock[] } | null> {
  try {
    const { data, error } = await (supabase as any)
      .from("content_serp_cache")
      .select("results, extracted_content, expires_at")
      .eq("node_id", nodeId).eq("query", query).maybeSingle();
    if (error || !data) return null;
    if (new Date(data.expires_at) > new Date()) {
      return { results: data.results || [], extractedContent: (data.extracted_content || []) as ContentBlock[] };
    }
  } catch { /* table may not exist */ }
  return null;
}

async function cacheSERP(nodeId: string, mapId: string, query: string, results: unknown[], extractedContent: ContentBlock[]): Promise<void> {
  try {
    const expiresAt = new Date(); expiresAt.setDate(expiresAt.getDate() + 30);
    await (supabase as any).from("content_serp_cache").upsert({
      node_id: nodeId, map_id: mapId, query, engine: "google",
      results, extracted_content: extractedContent,
      fetched_at: new Date().toISOString(), expires_at: expiresAt.toISOString(),
    }, { onConflict: "node_id,query" });
  } catch { /* cache write failed */ }
}

// =============================================
// Main SERP research — Pruning default, BM25 fallback
// =============================================

export async function researchSERP(nodeId: string, query: string, mapId: string): Promise<SERPResult> {
  logPublic("SERP", `======= START: "${query}" =======`);
  logPublic("SERP", getProxyStatusLine()); // ← visible in UI log panel even przy cache hit
  logDebug("SERP", `Crawl4AI URL: ${CRAWL4AI_URL}`);

  // Quick health check
  try {
    const healthResp = await fetch(`${CRAWL4AI_URL}/health`, { method: "GET" }).catch(() => null);
    logDebug("SERP", `Crawl4AI health: ${healthResp ? healthResp.status : "UNREACHABLE"}`);
  } catch {
    logError("SERP", `Crawl4AI health check FAILED — serwer prawdopodobnie nie działa!`);
  }

  const debug: SERPDebug = {
    query, urls_fetched: 0, urls_crawled: 0, urls_failed: 0,
    per_url: [], filter_method: "pruning", threshold_used: PRUNING_THRESHOLD,
    blocks_passed: 0, blocks_rejected: 0,
  };

  // 0. Domena wlasna projektu — NIGDY nie czytamy wlasnych artykulow
  // (przy regeneracji stara wersja tego samego artykulu rankuje w SERP)
  const ownDomain = await getOwnDomain(mapId);

  // 1. Cache check
  const cached = await getCachedSERP(nodeId, query);
  if (cached && ownDomain) {
    const before = cached.extractedContent.length;
    cached.extractedContent = cached.extractedContent.filter((b) => !isSameDomain(b.url, ownDomain));
    cached.results = (cached.results as any[]).filter((r: any) => !isSameDomain(r?.url || "", ownDomain));
    if (cached.extractedContent.length < before) {
      logPublic("SERP", `Cache: odfiltrowano ${before - cached.extractedContent.length} blok(i) z wlasnej domeny (${ownDomain})`);
    }
  }
  if (cached && cached.extractedContent.length > 0) {
    const cachedUrls = cached.results.map((r: any) => r.url || "").filter(Boolean);
    const totalChars = cached.extractedContent.reduce((s, b) => s + b.text.length, 0);
    // Build per_url from cached blocks so user sees which URLs were used
    const cachedPerUrl: URLDebug[] = cached.extractedContent.map((b) => ({
      url: b.url,
      crawled: true,
      chars_total: b.text.length,
      chars_kept: b.text.length,
    }));
    return {
      serpUrls: cachedUrls,
      cleanContent: cached.extractedContent,
      cacheHit: true, filterMethod: "cache",
      debug: {
        ...debug,
        filter_method: "cache",
        blocks_passed: cached.extractedContent.length,
        urls_fetched: cachedUrls.length,
        urls_crawled: cached.extractedContent.length,
        per_url: cachedPerUrl,
        total_chars_after: totalChars,
      } as any,
    };
  }

  // 2. SERP fetch + filter junk domains
  const serpResults = await getOrganicResults(query);
  const allUrls = (serpResults as any[])
    .filter((r) => r.url && r.type === "organic")
    .map((r) => r.url as string);

  const urls = allUrls.filter((url) => {
    if (shouldSkipUrl(url)) {
      logDebug("SERP", `SKIP: ${url.replace(/https?:\/\//, "").split("/")[0]} (blocked domain/extension)`);
      return false;
    }
    if (isSameDomain(url, ownDomain)) {
      logPublic("SERP", `SKIP: ${url.replace(/https?:\/\//, "").split("/")[0]} (wlasna domena projektu)`);
      return false;
    }
    return true;
  }).slice(0, MAX_SOURCES);

  debug.urls_fetched = urls.length;
  logPublic("SERP", `${allUrls.length} URLs z SERP → ${urls.length} po filtrze (${allUrls.length - urls.length} skipped)`);

  // 3. Content extraction (parallel via Crawl4AI proxy) — track per-URL
  const extractionResults = await Promise.all(
    urls.map(async (url) => {
      let result = await extractContent(url);
      if (!result.content) result = await simpleFetch(url);

      // T2F4 Content Cleaning — dedup lines + blacklist + min length
      let blacklistRemoved = 0;
      if (result.content) {
        const before = result.content.length;
        const cleanResult = cleanContent(result.content);
        result.content = cleanResult.cleaned;
        blacklistRemoved = cleanResult.blacklistRemoved;
        const after = result.content.length;
        if (before !== after) {
          logDebug("SERP", `T2F4 cleaned ${url.replace(/https?:\/\//, "").split("/")[0]}: ${before} → ${after} chars (${blacklistRemoved} blacklisted paragraphs removed)`);
        }
      }

      const urlDebug: URLDebug = {
        url,
        crawled: result.content != null && result.content.length > 0,
        chars_total: result.chars_total,
        chars_kept: result.content ? Math.min(result.content.length, MAX_CONTENT_CHARS) : 0,
        error: result.error,
        via_proxy: ("via_proxy" in result ? Boolean((result as any).via_proxy) : false) || undefined,
      };
      return { url, content: result.content, urlDebug };
    }),
  );

  debug.per_url = extractionResults.map((r) => r.urlDebug);
  debug.urls_crawled = extractionResults.filter((r) => r.content).length;
  debug.urls_failed = extractionResults.filter((r) => !r.content).length;

  // Proxy fallback batch summary — visible in logPublic for forensics
  const directSuccess = extractionResults.filter((r) => r.content && !r.urlDebug.via_proxy).length;
  const proxySuccess = extractionResults.filter((r) => r.content && r.urlDebug.via_proxy).length;
  const bothFailed = extractionResults.filter((r) => !r.content).length;
  debug.proxy_stats = {
    direct_success: directSuccess,
    proxy_success: proxySuccess,
    both_failed: bothFailed,
    proxy_available: PROXY_AVAILABLE,
  };
  logPublic(
    "SERP",
    `Crawl4AI batch summary: ${directSuccess} direct OK, ${proxySuccess} via GeoNode, ${bothFailed} failed ` +
    `(proxy ${PROXY_AVAILABLE ? "ENABLED" : "DISABLED"})`,
  );

  const validDocs = extractionResults
    .filter((e): e is { url: string; content: string; urlDebug: URLDebug } =>
      e.content != null && e.content.split(/\s+/).length >= MIN_BLOCK_WORDS);

  // 4. Filter: Pruning (default) → BM25 fallback
  let filteredBlocks: ContentBlock[];
  let filterMethod: string;

  // Try Pruning first (text density, no query needed — better for SERP research)
  filteredBlocks = validDocs
    .map((doc) => ({
      url: doc.url,
      text: doc.content.slice(0, MAX_CONTENT_CHARS),
      wordCount: doc.content.split(/\s+/).length,
      relevanceScore: pruningScore(doc.content),
    }))
    .filter((b) => b.wordCount >= MIN_BLOCK_WORDS && b.relevanceScore >= PRUNING_THRESHOLD)
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, 8);
  filterMethod = "pruning";
  debug.filter_method = "pruning";
  debug.threshold_used = PRUNING_THRESHOLD;

  // Fallback: BM25 if Pruning gives 0
  if (filteredBlocks.length === 0 && validDocs.length > 0) {
    const avgDocLen = validDocs.reduce((s, d) => s + tokenize(d.content).length, 0) / validDocs.length;
    filteredBlocks = validDocs
      .map((doc) => ({
        url: doc.url,
        text: doc.content.slice(0, MAX_CONTENT_CHARS),
        wordCount: doc.content.split(/\s+/).length,
        relevanceScore: computeBM25(query, doc.content, avgDocLen),
      }))
      .filter((b) => b.relevanceScore >= BM25_FALLBACK_THRESHOLD)
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 8);
    filterMethod = "bm25_fallback";
    debug.filter_method = "bm25_fallback";
    debug.threshold_used = BM25_FALLBACK_THRESHOLD;
  }

  // 5. Cross-block dedup (T2F4) — remove similar blocks, enforce char limit
  const totalCharsBefore = filteredBlocks.reduce((s, b) => s + b.text.length, 0);
  const dedupResult = deduplicateBlocks(filteredBlocks);
  const finalBlocks = dedupResult.kept;
  const totalCharsAfter = finalBlocks.reduce((s, b) => s + b.text.length, 0);

  if (dedupResult.removed > 0) {
    logPublic("SERP", `Cross-block dedup: ${filteredBlocks.length} → ${finalBlocks.length} blocks (${dedupResult.removed} removed, ${totalCharsBefore} → ${totalCharsAfter} chars)`);
  }

  debug.blocks_passed = finalBlocks.length;
  debug.blocks_rejected = validDocs.length - finalBlocks.length;
  (debug as any).cross_block_removed = dedupResult.removed;
  (debug as any).total_chars_before = totalCharsBefore;
  (debug as any).total_chars_after = totalCharsAfter;

  // 6. Cache
  await cacheSERP(nodeId, mapId, query, serpResults as unknown[], finalBlocks);

  return { serpUrls: urls, cleanContent: finalBlocks, cacheHit: false, filterMethod, debug };
}
